import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2, Palette, Search } from 'lucide-react'
import { useUpdateStoreSettings, type StoreSettings } from '../hooks/useStoreSettings'
import { toast } from 'sonner'

// Validation schema for widget settings
const widgetSchema = z.object({
  widgetPrimaryColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color'),
  widgetMode: z.enum(['dropdown', 'fullscreen']),
})

type WidgetFormData = z.infer<typeof widgetSchema>

interface WidgetPreviewProps {
  settings: StoreSettings | undefined
}

export function WidgetPreview({ settings }: WidgetPreviewProps) {
  const [previewColor, setPreviewColor] = useState(settings?.widgetPrimaryColor || '#3b82f6')

  const updateSettings = useUpdateStoreSettings()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<WidgetFormData>({
    resolver: zodResolver(widgetSchema),
    defaultValues: {
      widgetPrimaryColor: settings?.widgetPrimaryColor || '#3b82f6',
      widgetMode: settings?.widgetMode || 'dropdown',
    },
  })

  const watchedMode = watch('widgetMode')

  // Update preview when color changes
  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPreviewColor(e.target.value)
    setValue('widgetPrimaryColor', e.target.value, { shouldDirty: true })
  }

  const onSubmit = async (data: WidgetFormData) => {
    try {
      await updateSettings.mutateAsync(data)
      toast.success('Widget settings saved')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save widget settings')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="h-5 w-5" />
          Widget Configuration
        </CardTitle>
        <CardDescription>
          Customize the appearance of your search widget
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Primary Color */}
          <div className="space-y-2">
            <Label htmlFor="widgetPrimaryColor">Primary Color</Label>
            <div className="flex gap-2">
              <Input
                id="widgetPrimaryColor"
                type="text"
                {...register('widgetPrimaryColor')}
                onChange={handleColorChange}
                className="flex-1"
              />
              <Input
                type="color"
                value={previewColor}
                onChange={handleColorChange}
                className="w-12 h-10 p-1 cursor-pointer"
              />
            </div>
            {errors.widgetPrimaryColor && (
              <p className="text-sm text-destructive">{errors.widgetPrimaryColor.message}</p>
            )}
          </div>

          {/* Mode Select */}
          <div className="space-y-2">
            <Label>Display Mode</Label>
            <Select
              value={watchedMode}
              onValueChange={(value) => setValue('widgetMode', value as 'dropdown' | 'fullscreen', { shouldDirty: true })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="dropdown">Dropdown</SelectItem>
                <SelectItem value="fullscreen">Fullscreen</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {watchedMode === 'dropdown'
                ? 'Results appear below the search input'
                : 'Results open in a full-screen overlay'}
            </p>
          </div>

          {/* Preview Box */}
          <div className="space-y-2">
            <Label>Preview</Label>
            <div className="border border-border rounded-lg p-4 bg-muted/30">
              <div className="relative">
                <div
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border-2"
                  style={{ borderColor: previewColor }}
                >
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Search products...</span>
                </div>
                {/* Mock dropdown results */}
                <div className="mt-1 border border-border rounded-lg bg-background shadow-lg overflow-hidden">
                  <div className="p-2 text-xs text-muted-foreground border-b border-border">
                    Example Results
                  </div>
                  {['Safety Barrier', 'Mesh Panel', 'Warehouse Divider'].map((item, idx) => (
                    <div
                      key={item}
                      className="px-3 py-2 text-sm hover:bg-accent cursor-pointer flex items-center gap-3"
                      style={{
                        backgroundColor: idx === 0 ? `${previewColor}15` : undefined,
                      }}
                    >
                      <div
                        className="w-8 h-8 rounded bg-muted flex items-center justify-center text-xs"
                        style={{ backgroundColor: `${previewColor}20` }}
                      >
                        {item.charAt(0)}
                      </div>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <Button type="submit" disabled={updateSettings.isPending || !isDirty}>
            {updateSettings.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save Widget Settings
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
